/**
 * Vendor Portal Dashboard
 * Main dashboard for vendor management and self-service portal
 */
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  LinearProgress,
  IconButton,
  Tooltip,
  Rating,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
  Star as StarIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

// Component imports
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonButton from 'components/ArgonButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

// Services
import { vendorService, workOrderService } from "services/firestoreService";
import { useAuth } from "contexts/AuthContext";

const VendorDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({});
  const [vendors, setVendors] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);

  // Dialog states
  const [openVendorDialog, setOpenVendorDialog] = useState(false);
  const [openWorkOrderDialog, setOpenWorkOrderDialog] = useState(false);
  const [openOnboardingDialog, setOpenOnboardingDialog] = useState(false);

  // Form states
  const [vendorForm, setVendorForm] = useState({
    company_name: '',
    contact_email: '',
    contact_phone: '',
    business_type: '',
    service_radius_miles: 25,
    emergency_available: false,
    weekend_available: false
  });

  const [workOrderForm, setWorkOrderForm] = useState({
    vendor_id: '',
    title: '',
    description: '',
    priority: 'medium',
    estimated_cost: '',
    requested_date: ''
  });

  useEffect(() => {
    loadDashboardData();
    loadVendors();
    loadRecentWorkOrders();
  }, []);

  const loadDashboardData = async () => {
    try {
      const allVendors = await vendorService.getAll(user.uid);
      const allWorkOrders = await workOrderService.getAll(user.uid);
      const activeVendors = allVendors.filter(v => v.vendor_status === 'active');
      const pendingWOs = allWorkOrders.filter(wo => wo.status === 'pending');
      const completedWOs = allWorkOrders.filter(wo => wo.status === 'completed');
      const ratings = allVendors.map(v => v.rating || 0).filter(r => r > 0);
      const avgRating = ratings.length > 0 ? ratings.reduce((a, b) => a + b, 0) / ratings.length : 0;
      const paidInvoices = allWorkOrders.filter(wo => wo.invoice_status === 'paid');
      const totalRevenue = paidInvoices.reduce((sum, wo) => sum + (wo.estimated_cost || 0), 0);
      const pendingInvoices = allWorkOrders.filter(wo => wo.invoice_status === 'pending').length;

      setDashboardData({
        total_vendors: allVendors.length,
        active_vendors: activeVendors.length,
        pending_work_orders: pendingWOs.length,
        average_rating: avgRating,
        completed_jobs: completedWOs.length,
        pending_invoices: pendingInvoices,
        total_revenue: totalRevenue,
      });
    } catch (error) {
      console.error('Error loading vendor dashboard:', error);
    }
  };

  const loadVendors = async () => {
    try {
      const allVendors = await vendorService.getAll(user.uid);
      setVendors(allVendors.slice(0, 10));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadRecentWorkOrders = async () => {
    try {
      const allWorkOrders = await workOrderService.getAll(user.uid);
      setWorkOrders(allWorkOrders.slice(0, 10));
    } catch (error) {
      console.error('Error loading work orders:', error);
    }
  };

  const handleCreateVendor = async () => {
    try {
      await vendorService.create(vendorForm, user.uid);
      setOpenVendorDialog(false);
      setVendorForm({
        company_name: '',
        contact_email: '',
        contact_phone: '',
        business_type: '',
        service_radius_miles: 25,
        emergency_available: false,
        weekend_available: false
      });
      loadVendors();
      loadDashboardData();
    } catch (error) {
      console.error('Error creating vendor:', error);
    }
  };

  const handleCreateWorkOrder = async () => {
    try {
      await workOrderService.create(workOrderForm, user.uid);
      setOpenWorkOrderDialog(false);
      setWorkOrderForm({
        vendor_id: '',
        title: '',
        description: '',
        priority: 'medium',
        estimated_cost: '',
        requested_date: ''
      });
      loadRecentWorkOrders();
      loadDashboardData();
    } catch (error) {
      console.error('Error creating work order:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'info';
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'suspended': return 'error';
      case 'assigned': return 'info';
      case 'accepted': return 'primary';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'emergency': return 'error';
      default: return 'default';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      
      <ArgonBox py={3}>
        {/* Header */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <ArgonTypography variant="h4" fontWeight="medium">
              Vendor Portal
            </ArgonTypography>
            <ArgonTypography variant="body2" color="text">
              Manage vendors, work orders, and contractor relationships
            </ArgonTypography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button
                variant="outlined"
                startIcon={<BusinessIcon />}
                onClick={() => setOpenOnboardingDialog(true)}
                size="small"
              >
                Vendor Onboarding
              </Button>
              <Button
                variant="outlined"
                startIcon={<AssignmentIcon />}
                onClick={() => setOpenWorkOrderDialog(true)}
                size="small"
              >
                New Work Order
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenVendorDialog(true)}
                size="small"
              >
                Add Vendor
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Analytics Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <BusinessIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {dashboardData.total_vendors || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Vendors
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {dashboardData.active_vendors || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Active Vendors
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <ScheduleIcon color="warning" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {dashboardData.pending_work_orders || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Pending Jobs
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <StarIcon color="info" sx={{ mr: 2, fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {dashboardData.average_rating?.toFixed(1) || '0.0'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Avg Rating
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tables */}
        <Grid container spacing={3}>
          {/* Recent Vendors */}
          <Grid item xs={12} lg={6}>
            <Card>
              <Box p={3} display="flex" justifyContent="between" alignItems="center">
                <Typography variant="h6">Recent Vendors</Typography>
                <Button variant="outlined" size="small">View All</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Company</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Rating</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendors.slice(0, 5).map((vendor) => (
                      <TableRow key={vendor.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              {vendor.company_name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {vendor.company_name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {vendor.contact_email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={vendor.vendor_status}
                            color={getStatusColor(vendor.vendor_status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <Rating value={vendor.rating} readOnly size="small" />
                            <Typography variant="caption" ml={1}>
                              ({vendor.rating})
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => setSelectedVendor(vendor)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* Recent Work Orders */}
          <Grid item xs={12} lg={6}>
            <Card>
              <Box p={3} display="flex" justifyContent="between" alignItems="center">
                <Typography variant="h6">Recent Work Orders</Typography>
                <Button variant="outlined" size="small">View All</Button>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workOrders.slice(0, 5).map((workOrder) => (
                      <TableRow key={workOrder.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {workOrder.title}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {formatCurrency(workOrder.estimated_cost)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {workOrder.vendor?.company_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={workOrder.priority}
                            color={getPriorityColor(workOrder.priority)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={workOrder.status}
                            color={getStatusColor(workOrder.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>

        {/* Performance Metrics */}
        <Grid container spacing={3} mt={3}>
          <Grid item xs={12}>
            <Card>
              <Box p={3}>
                <Typography variant="h6" mb={2}>Performance Overview</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box p={2} bgcolor="grey.100" borderRadius={2}>
                      <Typography variant="caption" color="textSecondary" fontWeight="bold">
                        COMPLETED JOBS
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {dashboardData.completed_jobs || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        This month
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box p={2} bgcolor="info.lighter" borderRadius={2}>
                      <Typography variant="caption" color="textSecondary" fontWeight="bold">
                        PENDING INVOICES
                      </Typography>
                      <Typography variant="h4" color="info.main">
                        {dashboardData.pending_invoices || 0}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Awaiting approval
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box p={2} bgcolor="success.lighter" borderRadius={2}>
                      <Typography variant="caption" color="textSecondary" fontWeight="bold">
                        TOTAL REVENUE
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {formatCurrency(dashboardData.total_revenue)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Paid invoices
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Create Vendor Dialog */}
        <Dialog open={openVendorDialog} onClose={() => setOpenVendorDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Add New Vendor</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={vendorForm.company_name}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, company_name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  type="email"
                  value={vendorForm.contact_email}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, contact_email: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={vendorForm.contact_phone}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Business Type</InputLabel>
                  <Select
                    value={vendorForm.business_type}
                    label="Business Type"
                    onChange={(e) => setVendorForm(prev => ({ ...prev, business_type: e.target.value }))}
                  >
                    <MenuItem value="LLC">LLC</MenuItem>
                    <MenuItem value="Corporation">Corporation</MenuItem>
                    <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
                    <MenuItem value="Partnership">Partnership</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Service Radius (miles)"
                  type="number"
                  value={vendorForm.service_radius_miles}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, service_radius_miles: parseInt(e.target.value) }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenVendorDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateVendor} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>

        {/* Create Work Order Dialog */}
        <Dialog open={openWorkOrderDialog} onClose={() => setOpenWorkOrderDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Create Work Order</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Vendor</InputLabel>
                  <Select
                    value={workOrderForm.vendor_id}
                    label="Vendor"
                    onChange={(e) => setWorkOrderForm(prev => ({ ...prev, vendor_id: e.target.value }))}
                  >
                    {vendors.filter(v => v.vendor_status === 'active').map((vendor) => (
                      <MenuItem key={vendor.id} value={vendor.id}>
                        {vendor.company_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={workOrderForm.title}
                  onChange={(e) => setWorkOrderForm(prev => ({ ...prev, title: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={workOrderForm.description}
                  onChange={(e) => setWorkOrderForm(prev => ({ ...prev, description: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={workOrderForm.priority}
                    label="Priority"
                    onChange={(e) => setWorkOrderForm(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="emergency">Emergency</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Estimated Cost"
                  type="number"
                  value={workOrderForm.estimated_cost}
                  onChange={(e) => setWorkOrderForm(prev => ({ ...prev, estimated_cost: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenWorkOrderDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateWorkOrder} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>
      </ArgonBox>
      
      <Footer />
    </DashboardLayout>
  );
};

export default VendorDashboard;